import { z } from "zod";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";

import {
  parseFloatValue,
  parseIntValue,
  parseToDateValue,
} from "@/features/FormCard/utils";

import { Diff } from "@/features/FormCard/Diff";

import {
  AutocompleteField,
  DatePickerField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";

import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { dateSchema } from "@/features/FormCard/utils";

import { FormActionsUpdate } from "@/features/FormCard/Actions";

import { useEditForm } from "@/features/Edit/hooks";

import { choice_yes_no } from "@/data/choice.ts";
import { nace_2 } from "@/data/nace_2.ts";
import { rating_fitch } from "@/data/rating_fitch.ts";
import { rating_moodys } from "@/data/rating_moodys.ts";
import { rating_sp } from "@/data/rating_sp.ts";

const table_name = "a7_opcvm_issuer_ts";

const FormSchema = z.object({
  date: z.preprocess((val) => parseToDateValue(val as string), dateSchema),
  a7_opcvm_issuer_id: z.preprocess(
    (val) => parseInt(val as string, 10),
    z.number().int(),
  ),
  secteur_nace: z.string().nullable(),
  identifiant_fiscal_du_sous_jacent: z.string().nullable(),
  notation_moodys_contrepartie: z.string().nullable(),
  notation_fitch_contrepartie: z.string().nullable(),
  notation_sp_contrepartie: z.string().nullable(),
  chiffre_d_affaires_du_sous_jacent: z.preprocess(
    (val) => parseFloatValue(val as string),
    z.number().nullable(),
  ),
  nb_employes_contrepartie: z.preprocess(
    parseIntValue,
    z.number().int().nullable(),
  ),
  actionnaire_majoritaire_public: z.string().nullable(),
  cqs_contrepartie: z.preprocess(parseIntValue, z.number().int().nullable()),
});

export function A7OPCVMIssuerTSEditCard({ id }: { id: number }) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    id,
    table_name,
    FormSchema,
    client: "a7",
    disable_translations_request: true,
    fieldsNotApplyTranslation: [
      "notation_moodys_contrepartie",
      "notation_fitch_contrepartie",
      "notation_sp_contrepartie",
      "actionnaire_majoritaire_public",
    ],
  });

  return (
    <div className="grid place-items-center bg-gray-50 h-full">
      <div className="flex justify-center">
        <Card className="p-4 bg-white">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t(`${table_name}.title`, {
                  ns: "tables",
                  defaultValue: "Issuer time series",
                })}{" "}
                {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-2">
                <div className=" grid grid-cols-1 md:grid-cols-2 gap-2">
                  <DatePickerField
                    name="date"
                    label={t(`${table_name}.columns.date`, {
                      ns: "tables",
                      defaultValue: "Date",
                    })}
                    isRequired={true}
                    control={form.control}
                  />
                  <InputField
                    name="a7_opcvm_issuer_id"
                    label={t(`${table_name}.columns.a7_opcvm_issuer_id`, {
                      ns: "tables",
                      defaultValue: "Émetteur",
                    })}
                    isRequired={true}
                    control={form.control}
                    type="number"
                    allowNegative={false}
                  />
                </div>
                <div className=" grid grid-cols-1 md:grid-cols-1 gap-2">
                  <AutocompleteField
                    name="secteur_nace"
                    label={t(`${table_name}.columns.secteur_nace`, {
                      ns: "tables",
                      defaultValue: "Secteur",
                    })}
                    options={nace_2.sort((n1, n2) =>
                      (n1.value ?? "").localeCompare(n2.value ?? ""),
                    )}
                    control={form.control}
                  />
                </div>
                <div className=" grid grid-cols-1 md:grid-cols-2 gap-2">
                  <InputField
                    name="identifiant_fiscal_du_sous_jacent"
                    label={t(
                      `${table_name}.columns.identifiant_fiscal_du_sous_jacent`,
                      { ns: "tables", defaultValue: "Identifiant Fiscal" },
                    )}
                    control={form.control}
                  />
                </div>
                <div className=" grid grid-cols-1 md:grid-cols-2 gap-2">
                  <SelectField
                    name="notation_moodys_contrepartie"
                    label={t(
                      `${table_name}.columns.notation_moodys_contrepartie`,
                      { ns: "tables", defaultValue: "Notation Moody's" },
                    )}
                    options={rating_moodys.map((c) => ({
                      value: c.value,
                      label: c.value,
                    }))}
                    control={form.control}
                  />
                  <SelectField
                    name="notation_fitch_contrepartie"
                    label={t(
                      `${table_name}.columns.notation_fitch_contrepartie`,
                      { ns: "tables", defaultValue: "Notation Fitch" },
                    )}
                    options={rating_fitch.map((c) => ({
                      value: c.value,
                      label: c.value,
                    }))}
                    control={form.control}
                  />
                  <SelectField
                    name="notation_sp_contrepartie"
                    label={t(`${table_name}.columns.notation_sp_contrepartie`, {
                      ns: "tables",
                      defaultValue: "Notation S&P",
                    })}
                    options={rating_sp.map((c) => ({
                      value: c.value,
                      label: c.value,
                    }))}
                    control={form.control}
                  />
                </div>
                <div className=" grid grid-cols-1 md:grid-cols-2 gap-2">
                  <InputField
                    name="chiffre_d_affaires_du_sous_jacent"
                    label={t(
                      `${table_name}.columns.chiffre_d_affaires_du_sous_jacent`,
                      { ns: "tables", defaultValue: "Chiffre d'affaires" },
                    )}
                    control={form.control}
                  />
                  <InputField
                    name="nb_employes_contrepartie"
                    label={t(`${table_name}.columns.nb_employes_contrepartie`, {
                      ns: "tables",
                      defaultValue: "Nombre d'employés",
                    })}
                    control={form.control}
                  />
                </div>
                <div className=" grid grid-cols-1 md:grid-cols-2 gap-2">
                  <SelectField
                    name="actionnaire_majoritaire_public"
                    label={t(
                      `${table_name}.columns.actionnaire_majoritaire_public`,
                      { ns: "tables", defaultValue: "Actionnaire Majoritaire" },
                    )}
                    options={choice_yes_no.map((c) => ({
                      value: c.value,
                      label: c.value,
                    }))}
                    control={form.control}
                  />
                  <InputField
                    name="cqs_contrepartie"
                    label={t(`${table_name}.columns.cqs_contrepartie`, {
                      ns: "tables",
                      defaultValue: "CQS",
                    })}
                    control={form.control}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  elementId={id}
                  client="a7"
                  isDirty={form.formState.isDirty}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_update")}
          children={changedFields ? Diff(changedFields) : t("no_changes")}
        />

        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onDelete={closeDeleteModal}
        />
      </div>
    </div>
  );
}
